import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AdminWaTestSendPanel } from "@/components/admin/AdminWaTestSendPanel";
import { AdminWaFlowTriggerPanel } from "@/components/admin/AdminWaFlowTriggerPanel";
import { AdminWaWebhookSettingsCard } from "@/components/admin/AdminWaWebhookSettingsCard";
import { AdminWaCronPanel } from "@/components/admin/AdminWaCronPanel";
import {
  Loader2,
  Link2,
  MousePointerClick,
  ShoppingCart,
  CreditCard,
  CheckCircle2,
  IndianRupee,
  RefreshCw,
  TrendingUp,
} from "lucide-react";

const STAT_CARDS = [
  { key: "links_generated", label: "Links Generated", icon: Link2, color: "text-slate-700" },
  { key: "links_clicked", label: "Links Clicked", icon: MousePointerClick, color: "text-blue-600" },
  { key: "popup_opens", label: "Upgrade Popup Opens", icon: ShoppingCart, color: "text-amber-600" },
  { key: "checkout_starts", label: "Checkout Started", icon: CreditCard, color: "text-purple-600" },
  { key: "purchases", label: "Purchases Completed", icon: CheckCircle2, color: "text-emerald-600" },
  { key: "revenue", label: "WA Revenue (₹)", icon: IndianRupee, color: "text-emerald-700", isCurrency: true },
];

const EVENT_LABELS = {
  wa_link_generated: { label: "Link Generated", color: "bg-slate-100 text-slate-700" },
  wa_link_clicked: { label: "Link Clicked", color: "bg-blue-100 text-blue-700" },
  wa_auto_login_success: { label: "Auto Login", color: "bg-indigo-100 text-indigo-700" },
  upgrade_popup_opened_from_wa: { label: "Popup Opened", color: "bg-amber-100 text-amber-700" },
  checkout_started_from_wa: { label: "Checkout Started", color: "bg-purple-100 text-purple-700" },
  purchase_completed_from_wa: { label: "Purchase", color: "bg-emerald-100 text-emerald-700" },
};

export function AdminWaCampaignsTab({ setPinAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.waAnalytics();
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="wa-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12" data-testid="wa-empty">
        <p className="text-sm text-slate-500 mb-4">Failed to load WA analytics</p>
        <Button onClick={load} size="sm" variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  const summary = data.summary || {};
  const byStage = data.by_stage || {};
  const recent = data.recent_events || [];

  return (
    <div className="space-y-6" data-testid="wa-campaigns-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#18181B]" style={{ fontFamily: "Manrope, sans-serif" }}>
            WhatsApp Campaigns
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Attribution metrics for WA magic link campaigns
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="wa-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Webhook URL setting (editable) */}
      <AdminWaWebhookSettingsCard />

      {/* WA Cron — stage-day scheduler */}
      <AdminWaCronPanel setPinAction={setPinAction} />

      {/* Test send panel */}
      <AdminWaTestSendPanel onSent={load} />

      {/* WASimple Flow trigger panel */}
      <AdminWaFlowTriggerPanel onSent={load} />

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="wa-summary-cards">
        {STAT_CARDS.map((c) => {
          const val = summary[c.key] ?? 0;
          const display = c.isCurrency ? `₹${Number(val).toLocaleString("en-IN")}` : Number(val).toLocaleString("en-IN");
          return (
            <div
              key={c.key}
              className="bg-white rounded-xl border border-slate-200 p-4"
              data-testid={`wa-stat-${c.key}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <c.icon className={`h-4 w-4 ${c.color}`} />
                <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                  {c.label}
                </p>
              </div>
              <p className="text-2xl font-bold text-[#18181B]">{display}</p>
            </div>
          );
        })}
      </div>

      {/* Conversion funnel rates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wa-conversion-rates">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              Click Rate
            </p>
          </div>
          <p className="text-2xl font-bold text-[#18181B]">{summary.click_rate ?? 0}%</p>
          <p className="text-[11px] text-slate-500 mt-1">Clicks / Links generated</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
              Purchase Rate
            </p>
          </div>
          <p className="text-2xl font-bold text-[#18181B]">{summary.purchase_rate ?? 0}%</p>
          <p className="text-[11px] text-slate-500 mt-1">Purchases / Clicks</p>
        </div>
      </div>

      {/* Conversion by Download Stage */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="wa-by-stage">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-[#18181B]">Conversion by Download Stage</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            How users at different free-download stages convert
          </p>
        </div>
        {Object.keys(byStage).length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-8" data-testid="wa-stage-empty">
            No campaign data yet
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="wa-stage-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Stage</th>
                <th className="text-right px-4 py-2 font-medium">Generated</th>
                <th className="text-right px-4 py-2 font-medium">Clicked</th>
                <th className="text-right px-4 py-2 font-medium">Popup Opened</th>
                <th className="text-right px-4 py-2 font-medium">Purchased</th>
                <th className="text-right px-4 py-2 font-medium">Click Rate</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byStage).map(([stageKey, s]) => (
                <tr key={stageKey} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-semibold text-[#18181B]">
                    {stageKey === "stage_0" && "0 downloads"}
                    {stageKey === "stage_1" && "1 download"}
                    {stageKey === "stage_2" && "2 downloads"}
                    {!["stage_0", "stage_1", "stage_2"].includes(stageKey) && stageKey}
                  </td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{s.generated}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{s.clicked}</td>
                  <td className="px-4 py-2.5 text-right text-slate-700">{s.popup_opened}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">
                    {s.purchased}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-blue-600">
                    {s.click_rate}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent Events */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="wa-recent-events">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-[#18181B]">Recent Events</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">Latest 20 WA attribution events</p>
        </div>
        {recent.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-8" data-testid="wa-events-empty">
            No events yet
          </p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="wa-events-list">
            {recent.map((evt, i) => {
              const meta = EVENT_LABELS[evt.event_name] || {
                label: evt.event_name,
                color: "bg-slate-100 text-slate-700",
              };
              return (
                <li key={i} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold ${meta.color}`}
                    >
                      {meta.label}
                    </span>
                    <span className="text-[11px] text-slate-500 font-mono truncate">
                      {evt.token_id ? `${evt.token_id.slice(0, 8)}…` : "—"}
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400 shrink-0">{evt.timestamp}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
